// src/adapters/auth.ts
import { EnvConfig as EnvConfig2 } from "@repo/env-config";
import { storageService as storageService4 } from "@repo/storage";
import { getUpdatedToken, getWebsiteUrl } from "@repo/utils";

// src/coreApi.ts
import { EnvConfig } from "@repo/env-config";
import { storageService as storageService3 } from "@repo/storage";
import { getMobileAppVersion as getMobileAppVersion2 } from "@repo/utils";
import * as Sentry2 from "@sentry/browser";
import axios from "axios";
import mixpanel from "mixpanel-browser";

// src/refreshToken.ts
import { storageService as storageService2 } from "@repo/storage";
import { isMobileApp as isMobileApp2 } from "@repo/utils";
import * as Sentry from "@sentry/browser";

// src/mobileAppFunctionality.ts
import { storageService } from "@repo/storage";
import { getMobileAppVersion, isMobileApp } from "@repo/utils";
var MOBILE_FEATURE_NAMES = {
  burger: "MOBILE_APP_BURGER",
  depositFromHeader: "MOBILE_APP_DEPOSIT_FROM_HEADER",
  depositFromAccount: "MOBILE_APP_DEPOSIT_FROM_ACCOUNT",
  readyForToken: "WEB_IS_READY_FOR_TOKEN",
  requestNewToken: "MOBILE_APP_REQUEST_NEW_TOKEN",
  authState: "MOBILE_APP_AUTH_STATE",
  logout: "MOBILE_APP_LOGOUT",
  refreshToken: "MOBILE_APP_REFRESH_TOKEN",
  tokenVerification: "TOKEN_VERIFICATION",
  navLogin: "MOBILE_APP_NAV_LOGIN",
  navRegister: "MOBILE_APP_NAV_REGISTER",
  activeTab: "MOBILE_APP_ACTIVE_TAB",
  walletStatus: "MOBILE_APP_WALLET_STATUS",
  enablePushNotifications: "MOBILE_APP_ENABLE_PUSH_NOTIFICATIONS",
  createBankAccount: "MOBILE_APP_CREATE_BANK_ACCOUNT",
  goBack: "MOBILE_APP_BACK",
  cashback: "MOBILE_APP_CASHBACK",
  sportsbookStatus: "MOBILE_APP_SPORTSBOOK_STATUS"
};
var MOBILE_FEATURES = {
  [MOBILE_FEATURE_NAMES.burger]: {
    minimumVersion: 10
  },
  [MOBILE_FEATURE_NAMES.depositFromHeader]: {
    minimumVersion: 10
  },
  [MOBILE_FEATURE_NAMES.depositFromAccount]: {
    minimumVersion: 117
  },
  [MOBILE_FEATURE_NAMES.readyForToken]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.requestNewToken]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.authState]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.logout]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.refreshToken]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.tokenVerification]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.navLogin]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.navRegister]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.activeTab]: {
    minimumVersion: 52
  },
  [MOBILE_FEATURE_NAMES.walletStatus]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.enablePushNotifications]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.createBankAccount]: {
    minimumVersion: 15
  },
  [MOBILE_FEATURE_NAMES.goBack]: {
    minimumVersion: 164
  },
  [MOBILE_FEATURE_NAMES.cashback]: {
    minimumVersion: 176
  },
  [MOBILE_FEATURE_NAMES.sportsbookStatus]: {
    minimumVersion: 176
  }
};
var canUseMobileFeature = (featureKey) => {
  const version = getMobileAppVersion();
  if (!version) return false;
  const featureName = MOBILE_FEATURE_NAMES[featureKey];
  const feature = MOBILE_FEATURES[featureName];
  if (!feature) return false;
  const minimumVersion = feature.minimumVersion;
  const maximumVersion = "maximumVersion" in feature ? feature.maximumVersion : void 0;
  return (!minimumVersion || version >= minimumVersion) && (!maximumVersion || version <= maximumVersion);
};
var sendSportsbookStatusEvent = (openBetsCount) => {
  return sendMobileAppEvent("sportsbookStatus", { openBetsCount });
};
var sendMobileAppEvent = (featureKey, body = {}) => {
  if (!MOBILE_FEATURE_NAMES[featureKey]) {
    return false;
  }
  if (window.KTOMobileApp?.postMessage && canUseMobileFeature(featureKey)) {
    window.KTOMobileApp.postMessage(
      JSON.stringify({ event: MOBILE_FEATURE_NAMES[featureKey], ...body })
    );
    return true;
  }
  return false;
};
var parseQueryString = (search) => {
  const params = new URLSearchParams(search);
  const result = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};
var removeLocaleFromURL = (pathname) => {
  return pathname;
};
var updateMobileLocation = (location) => {
  if (!isMobileApp()) return;
  const slugs = JSON.parse(storageService.getValue("mobileTabsSlugs") || "{}");
  if (!slugs) return;
  const basePath = removeLocaleFromURL(location.pathname).split("/")[1];
  switch (basePath) {
    case slugs.virtualSportsSlug:
    case slugs.liveSportsSlug:
    case slugs.sportsSlug: {
      const whitelistedPages = ["overview", "live", "betHistory", "allSports"];
      const page = parseQueryString(location.search)?.page;
      if (page && whitelistedPages.includes(page)) {
        sendMobileAppEvent("activeTab", { active: page });
      } else if (!page) {
        if (basePath === slugs.liveSportsSlug) {
          sendMobileAppEvent("activeTab", { active: "live" });
        } else {
          sendMobileAppEvent("activeTab", { active: "overview" });
        }
      }
      break;
    }
    case slugs.liveCasinoSlug:
    case slugs.casinoSlug:
      sendMobileAppEvent("activeTab", { active: "casino" });
      break;
    case slugs.promotionsSlug:
      sendMobileAppEvent("activeTab", { active: "promotions" });
      break;
  }
};

// src/refreshToken.ts
var runRequestsQueue = async (requestsQueue2, http2, config, accessToken) => {
  requestsQueue2.forEach(({ config: config2, resolve, reject }) => {
    config2.headers = config2.headers || {};
    config2.headers.Authorization = `Bearer ${accessToken}`;
    http2.request(config2).then((response) => resolve(response)).catch((err) => reject(err));
  });
  await http2(config);
};
var refreshTokenMobileApp = async (requestsQueue2, http2, config) => {
  const newTokenListener = (e) => {
    window.removeEventListener(
      "MOBILE_APP_TOKEN",
      newTokenListener
    );
    const newAccessToken = e?.detail?.access_token;
    if (!newAccessToken) {
      Sentry.captureException(new Error("Mobile app did not provide a token"));
      return;
    }
    runRequestsQueue(requestsQueue2, http2, config, newAccessToken);
  };
  const accessToken = storageService2.getAccessToken();
  if (accessToken !== void 0 && accessToken !== null) {
    window.addEventListener(
      "MOBILE_APP_TOKEN",
      newTokenListener
    );
    sendMobileAppEvent("requestNewToken");
  }
};
var refreshTokenDefault = async (requestsQueue2, http2, config) => {
  const actualRefreshToken = storageService2.getRefreshToken();
  const accessToken = storageService2.getAccessToken();
  if (actualRefreshToken && accessToken) {
    const response = await doRefreshToken(actualRefreshToken, accessToken);
    if (response?.ok && response.data) {
      const { access_token, refresh_token } = response.data;
      storageService2.setTokens({
        access_token,
        refresh_token
      });
      runRequestsQueue(requestsQueue2, http2, config, access_token);
      return;
    }
    throw new Error("Could not refresh token");
  }
  throw new Error("No refresh token found");
};
var refreshToken = async (err, requestsQueue2, http2) => {
  const { config } = err;
  if (config.url === "/auth/signin" || !err.response) return;
  try {
    if (isMobileApp2()) {
      await refreshTokenMobileApp(requestsQueue2, http2, config);
    } else {
      await refreshTokenDefault(requestsQueue2, http2, config);
    }
  } catch (_error) {
    Sentry.captureException(_error);
    storageService2.clearTokens();
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
    throw _error;
  }
};

// src/types.ts
var ErrorCodes = {
  USER_LOCKED_TO_LOGIN: "USER_LOCKED_TO_LOGIN",
  WRONG_CREDENTIALS: "WRONG_CREDENTIALS"
};

// src/coreApi.ts
var HTTP_TIMEOUT = 3e4;
var requestsQueue = [];
var isRefreshing = false;
var getPackageVersion = () => "1.0.0";
var setPackageVersionGetter = (versionGetter) => {
  getPackageVersion = versionGetter;
};
var http = axios.create({
  baseURL: EnvConfig.API_URL,
  timeout: HTTP_TIMEOUT,
  headers: { "Content-Type": "application/json", Accept: "application/json" }
});
http.interceptors.request.use(async (req) => {
  const token = storageService3.getAccessToken();
  const isRefreshTokenEndpoint = req.url?.includes("/auth/refresh-token") ?? false;
  const mobileVersion = getMobileAppVersion2();
  if (mobileVersion) {
    req.headers["X-Requested-With"] = `KTOMobileApp_${mobileVersion}`;
  } else {
    req.headers["X-Requested-With"] = `KTOWeb_${getPackageVersion()}`;
  }
  if (token && !isRefreshTokenEndpoint) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return Promise.resolve(req);
});
http.interceptors.response.use(
  (response) => response,
  async (err) => {
    const isActivateAccountEndpoint = err?.config?.url?.includes("/auth/activate");
    const isRefreshTokenEndpoint = err?.config?.url?.includes(
      "/auth/refresh-token"
    );
    if (err.response && err.response.status === 401) {
      if (isRefreshTokenEndpoint) {
        Sentry2.captureException(err);
        storageService3.clearTokens();
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }
        try {
          mixpanel.reset();
        } catch {
          console.log("EventTracking: error resetting user identity");
        }
        return Promise.reject(err);
      }
      if (err.request.responseURL.indexOf("registration") < 0 && err.request.responseURL.indexOf("login") < 0 && err.request.responseURL.indexOf("password") < 0 && err.request.responseURL.indexOf("sign-in") < 0) {
        const errorCode = err.response.data.messageCode;
        if (errorCode !== ErrorCodes.USER_LOCKED_TO_LOGIN && errorCode !== ErrorCodes.WRONG_CREDENTIALS && !isActivateAccountEndpoint) {
          if (!isRefreshing) {
            isRefreshing = true;
            await refreshToken(err, requestsQueue, http);
            isRefreshing = false;
            requestsQueue = [];
          }
        }
        return new Promise((resolve, reject) => {
          requestsQueue.push({ config: err.config, resolve, reject });
        });
      }
    }
    return Promise.reject(err);
  }
);
var coreApi_default = http;

// src/adapterFunctions.ts
var defaultOptions = {
  hasNestedData: true,
  hasValid: false,
  hasNoData: false,
  api: coreApi_default
};
var generateGoodResponse = (response, options) => {
  const { hasNestedData, hasValid, hasNoData } = options;
  if (hasNoData) return { ok: true };
  const { data } = response;
  const ok = hasValid ? Boolean(data?.valid) : true;
  if (hasNestedData) {
    const nestedData = data?.data;
    return { ok, data: nestedData };
  }
  return { ok, data };
};
var generateBadResponse = (error) => {
  return {
    ok: false,
    error: error?.response?.data,
    errorDetails: error
  };
};
var apiGET = async (url, body, passedOptions = {}) => {
  const options = { ...defaultOptions, ...passedOptions };
  try {
    const response = await (options.api || coreApi_default).get(url, {
      headers: options.headers,
      ...body || {}
    });
    return generateGoodResponse(response, options);
  } catch (error) {
    return generateBadResponse(error);
  }
};
var apiPOST = async (url, body, passedOptions = {}, config) => {
  const options = { ...defaultOptions, ...passedOptions };
  try {
    const response = await (options.api || coreApi_default).post(url, body, config);
    return generateGoodResponse(response, options);
  } catch (error) {
    return generateBadResponse(error);
  }
};
var apiPUT = async (url, body, passedOptions = {}) => {
  const options = { ...defaultOptions, ...passedOptions };
  try {
    const response = await (options.api || coreApi_default).put(url, body);
    return generateGoodResponse(response, options);
  } catch (error) {
    return generateBadResponse(error);
  }
};
var apiPATCH = async (url, body, passedOptions = {}) => {
  const options = { ...defaultOptions, ...passedOptions };
  try {
    const response = await (options.api || coreApi_default).patch(url, body);
    return generateGoodResponse(response, options);
  } catch (error) {
    return generateBadResponse(error);
  }
};
var apiDELETE = async (url, body, passedOptions = {}) => {
  const options = { ...defaultOptions, ...passedOptions };
  try {
    const response = await (options.api || coreApi_default).delete(url, body || {});
    return generateGoodResponse(response, options);
  } catch (error) {
    return generateBadResponse(error);
  }
};
var fetcher = (url) => {
  return apiGET(url).then((response) => response.data);
};

// src/adapters/auth.ts
var COMPLETE_SITE_URL = getWebsiteUrl();
var LOGIN_2FA_EMAIL_LINK = `${COMPLETE_SITE_URL}verify2fa/`;
var doLogin = async (username, password, token, captchaSolution) => {
  const GAToken = getUpdatedToken("_ga");
  const GA_SESSION_ID = getUpdatedToken(EnvConfig2.GATSBY_GA_COOKIE ?? "");
  return await apiPOST("/auth/login", {
    username,
    password,
    token,
    captchaSolution,
    emailRedirectionLink: LOGIN_2FA_EMAIL_LINK,
    gaCookieSessionValue: GA_SESSION_ID,
    gaCookieValue: GAToken
  });
};
var doSocialLogin = async ({
  code,
  type,
  isIdToken
}) => {
  const GAToken = getUpdatedToken("_ga");
  const GA_SESSION_ID = getUpdatedToken(EnvConfig2.GATSBY_GA_COOKIE ?? "");
  return await apiPOST("/auth/social/sign-in", {
    code,
    type,
    isIdToken,
    gaCookieSessionValue: GA_SESSION_ID,
    gaCookieValue: GAToken
  });
};
var doLogout = async (reason = "") => {
  return await apiPOST("/auth/logout", reason, { hasNoData: true });
};
var getWallet = async (options = {}) => {
  return await apiGET("/wallet", null, options);
};
var doForgotPassword = async (email, returnUrl, token) => {
  return await apiPOST(
    "/auth/password/forgot/",
    {
      email,
      password_reset_url: returnUrl,
      token
    },
    { hasNoData: true }
  );
};
var doActivate = async (token) => {
  return await apiPOST(`/auth/activate/${token}/`, null, {
    hasNestedData: false
  });
};
var doVerifyEmail = async (token) => {
  return await apiPUT(`/auth/email/verify/${token}/`, null);
};
var doResetPassword = async (model) => {
  return apiPOST("/auth/password/reset/", model, { hasNoData: true });
};
var doCpfCheck = async ({
  cpf,
  token = null,
  nationality_country_id
}) => {
  return apiPOST(
    "/registration/cpf/check",
    {
      cpf,
      token,
      nationality_country_id
    },
    {
      hasNestedData: false,
      hasValid: true
    }
  );
};
var doStartRegistrationCpfCheck = async ({
  cpf,
  captcha_token = null,
  nationality_country_id,
  captcha_solution
}) => {
  return apiPOST(
    "/registration/cpf/check/v3",
    {
      cpf,
      captcha_token,
      nationality_country_id,
      captcha_solution
    },
    {
      hasNestedData: false
    }
  );
};
var getRegistrationCpfCheckStatus = async (afId, nationality_country_id) => {
  const params = { nationality_country_id };
  return apiGET(
    `/registration/cpf/check/v3/${afId}`,
    { params },
    { hasNestedData: false, hasValid: true }
  );
};
var doCafIframeCheck = async (onboardingId) => {
  return apiGET(`/documents/${onboardingId}`, null);
};
var doEmailCheck = async (email) => {
  return apiPOST(
    "/registration/email/check",
    { email },
    { hasNestedData: false, hasValid: true }
  );
};
var doSendValidationEmail = async ({
  email,
  national_id,
  language_code,
  captcha_token,
  captcha_solution
}) => {
  return apiPOST("/registration/email/send-token", {
    national_id,
    language_code,
    email,
    captcha_solution,
    captcha_token
  });
};
var doValidateSocialEmail = async ({
  email,
  national_id,
  language_code,
  captcha_token,
  captcha_solution
}) => {
  return apiPOST("/registration/email/mark-verified", {
    national_id,
    language_code,
    email,
    captcha_token,
    captcha_solution
  });
};
var doValidateEmail = async ({
  email,
  national_id,
  token,
  captcha_token,
  captcha_solution
}) => {
  return apiPOST("/registration/email/validate-token", {
    national_id,
    token,
    email,
    captcha_token,
    captcha_solution
  });
};
var doSendLoggedInValidationEmail = async () => {
  return apiPOST("/player/email/send-token", {});
};
var doLoggedInValidateEmail = async (token) => {
  return apiPOST("/player/email/validate-token", { token });
};
var doSendValidationSmsOnVerification = (domain_bound) => {
  return apiPOST("/player/mobile-number/send-collection-verification-sms", {
    domain_bound
  });
};
var doValidateSmsOnVerification = (verification_code) => {
  return apiPOST(
    "/player/mobile-number/check-collection-verification-sms-code",
    { verification_code }
  );
};
var doRegistrationSmsSend = async (body) => {
  return apiPOST("/registration/mobile-number/send-verification-sms", body);
};
var doRegistrationSmsValidation = async (body) => {
  return apiPOST(
    "/registration/mobile-number/check-verification-sms-code",
    body
  );
};
var doSendValidationSms = async (mobile_prefix, mobile_number, domain_bound) => {
  return apiPOST("/player/mobile-number/send-verification-sms", {
    mobile_prefix,
    mobile_number,
    domain_bound
  });
};
var doValidateSms = async (mobile_prefix, mobile_number, verification_code, national_id, user_id) => {
  return apiPOST("/player/mobile-number/check-verification-sms-code", {
    mobile_prefix,
    mobile_number,
    verification_code,
    national_id,
    user_id
  });
};
var doLoggedInValidateSms = async (verification_code) => {
  return apiPATCH("/mobile-verification/verify-verification-sms-code", {
    verification_code
  });
};
var doRegistration = async (model) => {
  const GAToken = getUpdatedToken("_ga");
  const GA_SESSION_ID = getUpdatedToken(EnvConfig2.GATSBY_GA_COOKIE ?? "");
  return await apiPOST("/registration/v4", {
    ...model,
    ga_cookie_value: GAToken,
    ga_cookie_session_value: GA_SESSION_ID
  });
};
var doRefreshToken = async (refreshToken2, accessToken) => {
  return await apiPOST("/auth/refresh-token", {
    refresh_token: refreshToken2,
    auth_token: accessToken,
    login_type: storageService4.getLoginType()
  });
};
var getCurrentUser = async () => {
  return await apiGET("/user");
};
var getCurrentSessionId = async () => {
  return await apiGET("/session/active");
};
var countryCheckTranslations = async () => {
  return await apiGET("/country/check");
};
var doBirthdayCheck = async (cpf, dateOfBirth) => {
  return await apiPOST("/dob-check", { cpf, dateOfBirth }, { hasValid: true });
};
var doResendEmail = async (playerEmail, recaptchaToken) => {
  return await apiPOST(`/account-verification/${playerEmail}/send-email`, {
    emailRedirectionLink: LOGIN_2FA_EMAIL_LINK,
    token: recaptchaToken
  });
};
var getIs2faValidated = async (playerEmail) => {
  return await apiGET(`/account-verification/${playerEmail}/is-token-validated`);
};
var doValidate2fa = async (token) => {
  return await apiPOST(`/account-verification/${token}/validate-token`);
};
var getOnboardingState = async (token) => {
  return await apiGET(`/liveness/${token}`);
};
var getForgotPasswordOnboarding = async (token) => {
  return await apiGET(`/auth/onboarding/${token}`);
};
var doGenerateNewForgotPasswordOnboarding = async (token, recaptchaToken, email) => {
  return await apiPOST(`/auth/onboarding/${token}`, {
    recaptcha_token: recaptchaToken,
    email
  });
};
var getOnboardingIdAndUrl = async (token) => {
  return await apiGET(`/auth/liveness/${token}`);
};
var doGenerateOnboarding = async (livenessType, playerId, reason) => {
  return await apiPOST("/liveness", {
    livenessType,
    playerId,
    reason
  });
};
var getMigrateableStatus = async ({
  email,
  recaptchaToken
}) => {
  return await apiPOST("/registration/user/is-migrateable", {
    recaptcha_token: recaptchaToken,
    email
  });
};
var doAccountReOpenProcess = async (model) => {
  return await apiPOST("/account-reopen", model);
};
var sendEmailVerificationCode = async (email) => {
  return await apiPOST("/account-verification/email/send-email", {
    email
  });
};
var validateEmailVerificationCode = async (email, token) => {
  return await apiPOST("/account-verification/email/validate-token", {
    email,
    token
  });
};
var sendSmsVerificationCode = async (email, domain_bound) => {
  return await apiPOST(
    "/account-verification/mobile-number/send-verification-sms",
    {
      email,
      domain_bound
    }
  );
};
var validateSmsVerificationCode = async (email, verification_code) => {
  return await apiPOST(
    "/account-verification/mobile-number/check-verification-sms-code",
    {
      email,
      verification_code
    }
  );
};
var doSendResetPasswordEmailCode = async (email) => {
  return await apiPOST("/player/password/forgot/email/send-email", {
    email
  });
};
var doValidateResetPasswordEmailCode = async (email, token) => {
  return await apiPOST("/player/password/forgot/email/validate-token", {
    email,
    token
  });
};
var doSendResetPasswordSMSCode = async (mobile_prefix, mobile_number) => {
  return await apiPOST(
    "/player/password/forgot/mobile-number/send-verification-sms",
    {
      mobile_prefix,
      mobile_number,
      domain_bound: true
    }
  );
};
var doValidateResetPasswordSMSCode = async (mobile_prefix, mobile_number, verification_code) => {
  return await apiPOST(
    "/player/password/forgot/mobile-number/check-verification-sms-code",
    {
      mobile_prefix,
      mobile_number,
      verification_code
    }
  );
};
var doRequestResetPasswordLiveness = async (token, userId) => {
  return await apiPOST("/player/password/forgot/onboarding", {
    token,
    user_id: userId
  });
};
var doResetPasswordV2 = async ({
  token,
  userId,
  onboardingType,
  password,
  confirmPassword
}) => {
  return await apiPOST("/player/password/forgot", {
    token,
    user_id: userId,
    onboarding_type: onboardingType,
    password,
    confirm_password: confirmPassword
  });
};

export {
  doLogin,
  doSocialLogin,
  doLogout,
  getWallet,
  doForgotPassword,
  doActivate,
  doVerifyEmail,
  doResetPassword,
  doCpfCheck,
  doStartRegistrationCpfCheck,
  getRegistrationCpfCheckStatus,
  doCafIframeCheck,
  doEmailCheck,
  doSendValidationEmail,
  doValidateSocialEmail,
  doValidateEmail,
  doSendLoggedInValidationEmail,
  doLoggedInValidateEmail,
  doSendValidationSmsOnVerification,
  doValidateSmsOnVerification,
  doRegistrationSmsSend,
  doRegistrationSmsValidation,
  doSendValidationSms,
  doValidateSms,
  doLoggedInValidateSms,
  doRegistration,
  doRefreshToken,
  getCurrentUser,
  getCurrentSessionId,
  countryCheckTranslations,
  doBirthdayCheck,
  doResendEmail,
  getIs2faValidated,
  doValidate2fa,
  getOnboardingState,
  getForgotPasswordOnboarding,
  doGenerateNewForgotPasswordOnboarding,
  getOnboardingIdAndUrl,
  doGenerateOnboarding,
  getMigrateableStatus,
  doAccountReOpenProcess,
  sendEmailVerificationCode,
  validateEmailVerificationCode,
  sendSmsVerificationCode,
  validateSmsVerificationCode,
  doSendResetPasswordEmailCode,
  doValidateResetPasswordEmailCode,
  doSendResetPasswordSMSCode,
  doValidateResetPasswordSMSCode,
  doRequestResetPasswordLiveness,
  doResetPasswordV2,
  MOBILE_FEATURE_NAMES,
  MOBILE_FEATURES,
  canUseMobileFeature,
  sendSportsbookStatusEvent,
  sendMobileAppEvent,
  updateMobileLocation,
  refreshToken,
  ErrorCodes,
  setPackageVersionGetter,
  coreApi_default,
  apiGET,
  apiPOST,
  apiPUT,
  apiPATCH,
  apiDELETE,
  fetcher
};
